/* línkè · 图片处理（2026-05-26）
   File → Canvas 压缩到长边 1280px → JPEG base64
   iOS Safari 选择 HEIC 时会自动转 JPEG，一般无需特别处理 */

const DEFAULT_MAX_EDGE = 1280;
const DEFAULT_QUALITY = 0.85;
const MAX_INPUT_SIZE = 20 * 1024 * 1024; // 20MB 原图上限

/**
 * 把用户选的图压缩 + 转 base64
 * @param {File} file
 * @param {{maxEdge?:number, quality?:number, square?:boolean}} opts
 *   square=true → 先按中心裁成正方形再缩放（头像用：不裁的话套进圆框会被挤扁）
 * @returns {Promise<{media_type:'image/jpeg', data:string, preview:string, bytes:number}>}
 */
export async function processImage(file, opts = {}) {
  if (!file) throw new Error('没选图片');
  if (!file.type.startsWith('image/')) {
    throw new Error('只支持图片文件');
  }
  if (file.size > MAX_INPUT_SIZE) {
    throw new Error(`图片太大了（${(file.size / 1024 / 1024).toFixed(1)} MB）—— 请用 20 MB 以内的`);
  }

  const maxEdge = opts.maxEdge || DEFAULT_MAX_EDGE;
  const quality = opts.quality || DEFAULT_QUALITY;

  const img = await readImageFromFile(file);

  const canvas = document.createElement('canvas');
  const ctx0 = canvas.getContext('2d');
  if (!ctx0) throw new Error('Canvas 不可用');

  if (opts.square) {
    // 中心裁方：取原图短边做正方形，再缩到 maxEdge。QQ 式圆头像就是这么来的——
    // 直接把长方形塞进圆框会挤扁人脸。
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const side = Math.min(w, h);
    const sx = Math.round((w - side) / 2);
    const sy = Math.round((h - side) / 2);
    const out = Math.min(side, maxEdge);
    canvas.width = out;
    canvas.height = out;
    ctx0.drawImage(img, sx, sy, side, side, 0, 0, out, out);
  } else {
    const { width, height } = computeResize(img, maxEdge);
    canvas.width = width;
    canvas.height = height;
    ctx0.drawImage(img, 0, 0, width, height);
  }

  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  const base64 = dataUrl.split(',')[1] || '';
  if (!base64) throw new Error('图片压缩失败');

  return {
    media_type: 'image/jpeg',
    data: base64,
    preview: dataUrl,
    bytes: Math.floor((base64.length * 3) / 4),
  };
}

function readImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () =>
        reject(new Error('图片解码失败 —— 可能格式不支持（iPhone 用户：直接从相册选最稳）'));
      img.src = reader.result;
    };
    reader.onerror = () => reject(new Error('读不到这张图'));
    reader.readAsDataURL(file);
  });
}

function computeResize(img, maxEdge) {
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (w <= maxEdge && h <= maxEdge) return { width: w, height: h };
  if (w >= h) {
    return { width: maxEdge, height: Math.round((h * maxEdge) / w) };
  }
  return { width: Math.round((w * maxEdge) / h), height: maxEdge };
}

/** Anthropic vision API 的 image block 格式。 */
export function buildImageBlock({ media_type, data }) {
  return {
    type: 'image',
    source: { type: 'base64', media_type, data },
  };
}

/** 把 {text, images} 组装成 content array（Anthropic 多模态）。 */
export function buildContentArray(text, images) {
  const blocks = [];
  // 按 Anthropic 推荐：image 在前，text 在后（视觉先看图再读字）
  for (const img of images || []) {
    blocks.push(buildImageBlock(img));
  }
  if (text && text.trim()) {
    blocks.push({ type: 'text', text });
  }
  return blocks;
}

/** db 存的 content 可能是字符串 or JSON 数组字符串。解析后给 UI 用。 */
export function parseMessageContent(raw) {
  if (raw == null) return { text: '', images: [] };
  if (typeof raw !== 'string') {
    // 已经是 array of blocks
    if (Array.isArray(raw)) return blocksToParts(raw);
    return { text: String(raw), images: [] };
  }
  // try parse JSON array
  const trimmed = raw.trim();
  if (trimmed.startsWith('[')) {
    try {
      const arr = JSON.parse(trimmed);
      if (Array.isArray(arr)) return blocksToParts(arr);
    } catch {}
  }
  return { text: raw, images: [] };
}

function blocksToParts(blocks) {
  let text = '';
  const images = [];
  for (const b of blocks) {
    if (!b) continue;
    if (b.type === 'text' && typeof b.text === 'string') {
      text = text ? text + '\n' + b.text : b.text;
    } else if (b.type === 'image' && b.source?.data) {
      images.push({
        media_type: b.source.media_type || 'image/jpeg',
        data: b.source.data,
      });
    }
  }
  return { text, images };
}
